import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Button } from "./ui/button";
import { StatusBadge, PriorityBadge } from "./badges";
import { Search, FolderKanban, CheckSquare, AlertCircle, FileText } from "lucide-react";

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ projects: [], tasks: [], issues: [], requirements: [] });
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults({ projects: [], tasks: [], issues: [], requirements: [] });
      return;
    }
    timer.current = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => setResults(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = (path) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };

  const hasResults = results.projects.length || results.tasks.length || results.issues.length || results.requirements.length;

  return (
    <>
      <Button variant="outline" size="sm" data-testid="global-search-btn" onClick={() => setOpen(true)}
        className="text-muted-foreground gap-2 hidden sm:flex">
        <Search className="h-3.5 w-3.5" />
        <span className="text-xs">Search...</span>
        <kbd className="font-mono text-[9px] border border-border rounded px-1 py-0.5 bg-accent">⌘K</kbd>
      </Button>
      <Button variant="ghost" size="icon" className="sm:hidden" data-testid="global-search-btn-mobile" onClick={() => setOpen(true)}>
        <Search className="h-4 w-4" />
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false} data-testid="global-search-dialog">
        <CommandInput placeholder="Search projects, tasks, issues, requirements..." value={q} onValueChange={setQ} data-testid="global-search-input" />
        <CommandList>
          <CommandEmpty>{q.trim().length < 2 ? "Type at least 2 characters..." : "No results found."}</CommandEmpty>
          {results.projects.length > 0 && (
            <CommandGroup heading="Projects">
              {results.projects.map((p) => (
                <CommandItem key={p.id} value={p.id} onSelect={() => go(`/projects/${p.id}`)} data-testid="search-result-project">
                  <FolderKanban className="h-4 w-4 text-blue-500" />
                  <span className="font-mono text-xs text-primary">{p.key}</span>
                  <span className="truncate">{p.name}</span>
                  <StatusBadge status={p.status} />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.tasks.length > 0 && (
            <CommandGroup heading="Tasks">
              {results.tasks.map((t) => (
                <CommandItem key={t.id} value={t.id} onSelect={() => go(`/projects/${t.project_id}?task=${t.id}`)} data-testid="search-result-task">
                  <CheckSquare className="h-4 w-4 text-emerald-500" />
                  <span className="font-mono text-xs text-primary">{t.tid}</span>
                  <span className="truncate">{t.title}</span>
                  <StatusBadge status={t.status} />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.issues.length > 0 && (
            <CommandGroup heading="Issues">
              {results.issues.map((i) => (
                <CommandItem key={i.id} value={i.id} onSelect={() => go(`/projects/${i.project_id}?tab=issues`)} data-testid="search-result-issue">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <span className="font-mono text-xs text-primary">{i.project_key} #{i.number}</span>
                  <span className="truncate">{i.title}</span>
                  <PriorityBadge priority={i.priority} />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results.requirements.length > 0 && (
            <CommandGroup heading="Requirements">
              {results.requirements.map((r) => (
                <CommandItem key={r.id} value={r.id} onSelect={() => go(`/projects/${r.project_id}?tab=requirements`)} data-testid="search-result-requirement">
                  <FileText className="h-4 w-4 text-indigo-500" />
                  <span className="font-mono text-xs text-primary">{r.project_key} {r.rid}</span>
                  <span className="truncate">{r.title}</span>
                  <StatusBadge status={r.status} />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
